"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useRouter } from "next/navigation";

interface User {
  id: string;
  email: string;
  username: string;
  role: "admin" | "user";
}

interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  isLoading: boolean;
  tokenExpiresAt: Date | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tokenExpiresAt, setTokenExpiresAt] = useState<Date | null>(null);
  const router = useRouter();

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch("/api/auth/me", {
        credentials: "include",
      });

      if (response.ok) {
        const userData = await response.json();
        setUser({ ...userData, role: userData.role || "user" });
        if (userData.tokenExpiresAt) {
          setTokenExpiresAt(new Date(userData.tokenExpiresAt));
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, [router]);

  const refreshSession = useCallback(async () => {
    try {
      const response = await fetch("/api/auth/me", {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setUser({ ...data, role: data.role || "user" });
        if (data.tokenExpiresAt) {
          setTokenExpiresAt(new Date(data.tokenExpiresAt));
        }
      } else if (response.status === 401) {
        setUser(null);
        setTokenExpiresAt(null);
      }
    } catch {
      // Silent fail for network errors — countdown will handle expiry
    }
  }, []);

  // Track user activity — only refresh session when user is actively using the app
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    const markActive = () => {
      lastActivityRef.current = Date.now();
    };

    window.addEventListener("click", markActive);
    window.addEventListener("keydown", markActive);

    return () => {
      window.removeEventListener("click", markActive);
      window.removeEventListener("keydown", markActive);
    };
  }, []);

  // Proactively clear session when token expires
  useEffect(() => {
    if (!user || !tokenExpiresAt) return;

    const msUntilExpiry = tokenExpiresAt.getTime() - Date.now();
    if (msUntilExpiry <= 0) {
      setUser(null);
      setTokenExpiresAt(null);
      return;
    }

    const timeout = setTimeout(() => {
      setUser(null);
      setTokenExpiresAt(null);
    }, msUntilExpiry);

    return () => clearTimeout(timeout);
  }, [user, tokenExpiresAt]);

  // Periodically refresh session only if user was active recently
  useEffect(() => {
    if (!user) return;

    const ACTIVITY_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes
    const REFRESH_INTERVAL_MS = 4 * 60 * 1000; // 4 minutes — must be shorter than threshold

    const interval = setInterval(() => {
      if (Date.now() - lastActivityRef.current < ACTIVITY_THRESHOLD_MS) {
        refreshSession();
      }
    }, REFRESH_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Always verify auth when tab becomes visible — token may have expired while away
        refreshSession();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [user, refreshSession]);

  const login = useCallback(
    async (email: string, password: string) => {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Login failed");
      }

      // Check auth after successful login
      await checkAuth();
      router.push("/");
    },
    [checkAuth, router],
  );

  const logout = useCallback(async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
      router.push("/login");
    }
  }, [router]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const isAdmin = user?.role === "admin";

  const contextValue = useMemo(
    () => ({
      user,
      isAdmin,
      isLoading,
      tokenExpiresAt,
      login,
      logout,
      checkAuth,
    }),
    [user, isAdmin, isLoading, tokenExpiresAt, login, logout, checkAuth],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
